/**
 * NinjaOne and ConnectSecure (task 161, Stories 42–43): the platform
 * credential card and the per-Client mapping modal. The credential is
 * Simulint's, one per vendor; each Client is then mapped to one of the
 * vendor's organizations (NinjaOne) or companies (ConnectSecure), and one
 * organization can belong to one Client only (ADR-0026 §5).
 *
 * Both forms are write-only: nothing stored is ever shown back.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deletePlatformCredential,
  listVendorAccounts,
  putPlatformCredential,
  type ClientTenant,
  type ConnectSecureCredentialInput,
  type NinjaOneCredentialInput,
  type PlatformIntegrationName,
  type PlatformIntegrationStatus,
} from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ConfirmModal } from './ConfirmModal';
import {
  ConnectErrorNote,
  ErrorNote,
  INPUT,
  ModalShell,
  sourceLabel,
  timeAgo,
} from '@bluesphere/mdr-alert-panel';
import { Loading } from './states';

const NOUN: Record<PlatformIntegrationName, string> = {
  ninjaone: 'organization',
  connectsecure: 'company',
};

export function vendorNoun(name: PlatformIntegrationName): string {
  return NOUN[name];
}

/** The mapped organization's name from a Client's registration, if any. */
export function mappedName(tenant: ClientTenant, name: PlatformIntegrationName): string | null {
  const reg = tenant.sources[name];
  if (!reg || reg.state !== 'enabled') return null;
  const key = name === 'ninjaone' ? 'organization_name' : 'company_name';
  return reg.config?.[key] ?? null;
}

// ----- The platform credential ------------------------------------------------

export function PlatformCredentialCard({
  name,
  status,
  statusError,
  canManage,
  idToken,
}: {
  name: PlatformIntegrationName;
  status: PlatformIntegrationStatus | undefined;
  statusError: unknown;
  canManage: boolean;
  idToken: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['platform-integration', name] });

  const save = useMutation<
    PlatformIntegrationStatus,
    Error,
    NinjaOneCredentialInput | ConnectSecureCredentialInput
  >({
    mutationFn: (credential) =>
      name === 'ninjaone'
        ? putPlatformCredential('ninjaone', credential as NinjaOneCredentialInput, idToken)
        : putPlatformCredential(
            'connectsecure',
            credential as ConnectSecureCredentialInput,
            idToken,
          ),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
  });
  const remove = useMutation<PlatformIntegrationStatus, Error, void>({
    mutationFn: () => deletePlatformCredential(name, idToken),
    onSuccess: () => {
      setRemoving(false);
      refresh();
    },
  });

  const label = sourceLabel(name);
  const mapped = status?.mapped_clients ?? 0;

  return (
    <div className="bs-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-bold lg:text-2xl">Platform credential</h2>
          <p className="mt-1 max-w-3xl text-sm text-bluesphere-textSecondary">
            One {label} credential for the whole platform, not per Client. It is checked against{' '}
            {label} before it is saved and never shown again.
          </p>
        </div>
        {canManage && status && (
          <div className="flex flex-wrap items-center gap-3">
            <PillButton size="sm" onClick={() => setEditing(true)}>
              {status.connected ? 'Rotate credential' : `Connect ${label}`}
            </PillButton>
            {status.connected && (
              <button
                type="button"
                onClick={() => setRemoving(true)}
                disabled={mapped > 0}
                className="text-xs text-red-700 underline hover:text-red-900 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
                title={
                  mapped > 0
                    ? `Unmap the ${mapped} Client${mapped === 1 ? '' : 's'} below first.`
                    : undefined
                }
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
      <div className="mt-3 text-sm">
        {status ? (
          status.connected ? (
            <span className="text-bluesphere-textOnLight">
              Connected
              {status.credential_changed_at && (
                <span className="text-bluesphere-muted">
                  {' '}
                  · last changed {timeAgo(status.credential_changed_at)}
                </span>
              )}
              <span className="text-bluesphere-muted">
                {' '}
                · {mapped} Client{mapped === 1 ? '' : 's'} mapped
              </span>
            </span>
          ) : (
            <span className="text-bluesphere-muted">
              Not connected. {canManage ? '' : 'An Admin with all Clients in scope connects it.'}
            </span>
          )
        ) : statusError ? (
          <ErrorNote error={statusError} />
        ) : (
          <Loading height="h-6" />
        )}
      </div>

      {editing && (
        <CredentialModal
          name={name}
          rotating={Boolean(status?.connected)}
          onCancel={() => {
            setEditing(false);
            save.reset();
          }}
          onSubmit={(credential) => save.mutate(credential)}
          isPending={save.isPending}
          error={save.error}
        />
      )}
      {removing && (
        <ConfirmModal
          title={`Remove the ${label} credential?`}
          confirmLabel="Remove credential"
          pendingLabel="Removing…"
          onCancel={() => {
            setRemoving(false);
            remove.reset();
          }}
          onConfirm={() => remove.mutate()}
          isPending={remove.isPending}
          error={remove.error}
        >
          <p className="text-sm text-bluesphere-textSecondary">
            The stored credential is deleted immediately. No Client is mapped, so nothing stops
            syncing.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
}

function CredentialModal({
  name,
  rotating,
  onCancel,
  onSubmit,
  isPending,
  error,
}: {
  name: PlatformIntegrationName;
  rotating: boolean;
  onCancel: () => void;
  onSubmit: (credential: NinjaOneCredentialInput | ConnectSecureCredentialInput) => void;
  isPending: boolean;
  error: unknown;
}) {
  const [region, setRegion] = useState<NinjaOneCredentialInput['region']>('app');
  const [pod, setPod] = useState('');
  const [tenant, setTenant] = useState('');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const ninja = name === 'ninjaone';
  const filled =
    clientId.trim().length > 0 &&
    secret.trim().length > 0 &&
    (ninja || (pod.trim().length > 0 && tenant.trim().length > 0));
  const canSubmit = filled && !isPending;
  const label = sourceLabel(name);

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          {rotating ? `Rotate the ${label} credential` : `Connect ${label}`}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          {ninja
            ? 'An API Services application created by a NinjaOne system admin under Administration → Apps → API, with the Monitoring and Management scopes.'
            : 'An API key generated for a ConnectSecure user under Global → Settings → Users → API Key.'}{' '}
          It is checked before it is saved{rotating ? ' and replaces the stored one' : ''}.
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          onSubmit(
            ninja
              ? { region, client_id: clientId.trim(), client_secret: secret.trim() }
              : {
                  pod: pod.trim().toLowerCase(),
                  tenant: tenant.trim(),
                  client_id: clientId.trim(),
                  client_secret: secret.trim(),
                },
          );
        }}
        className="space-y-4 px-6 py-5"
        autoComplete="off"
      >
        {ninja ? (
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
              Region
            </span>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value as NinjaOneCredentialInput['region'])}
              className={INPUT}
            >
              <option value="app">North America (app.ninjarmm.com)</option>
              <option value="eu">Europe (eu.ninjarmm.com)</option>
              <option value="oc">Oceania (oc.ninjarmm.com)</option>
            </select>
          </label>
        ) : (
          <>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
                Pod
              </span>
              <input
                value={pod}
                onChange={(e) => setPod(e.target.value)}
                placeholder="pod104.myconnectsecure.com"
                className={INPUT}
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
                Tenant
              </span>
              <input value={tenant} onChange={(e) => setTenant(e.target.value)} className={INPUT} />
            </label>
          </>
        )}
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Client ID
          </span>
          <input value={clientId} onChange={(e) => setClientId(e.target.value)} className={INPUT} />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Client secret
          </span>
          <input
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            autoComplete="new-password"
            className={INPUT}
          />
        </label>
        <ErrorNote error={error} />
        <div className="flex items-center justify-end gap-2 pt-2">
          <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </PillButton>
          <PillButton size="sm" type="submit" disabled={!canSubmit}>
            {isPending ? 'Validating…' : 'Validate and save'}
          </PillButton>
        </div>
      </form>
    </ModalShell>
  );
}

// ----- The per-Client mapping --------------------------------------------------

export function VendorMappingModal({
  name,
  tenant,
  idToken,
  onCancel,
  onSubmit,
  isPending,
  error,
  canAddService = false,
  onAddService = () => {},
}: {
  name: PlatformIntegrationName;
  tenant: ClientTenant;
  idToken: string;
  onCancel: () => void;
  onSubmit: (externalId: string) => void;
  isPending: boolean;
  error: unknown;
  /** A platform Admin may add the covering service on 409 `service_required` (task 179c). */
  canAddService?: boolean;
  onAddService?: () => void;
}) {
  const accounts = useQuery({
    queryKey: ['vendor-accounts', name],
    queryFn: () => listVendorAccounts(name, idToken),
    enabled: Boolean(idToken),
    staleTime: 60_000,
  });
  const current = tenant.sources[name];
  const currentId =
    current?.state === 'enabled'
      ? current.config?.[name === 'ninjaone' ? 'organization_id' : 'company_id']
      : undefined;
  const [selected, setSelected] = useState<string>(currentId ?? '');
  const noun = vendorNoun(name);
  const label = sourceLabel(name);
  const chosen = accounts.data?.items.find((a) => a.id === selected);
  const takenByOther = Boolean(
    chosen?.mapped_to && chosen.mapped_to.tenant_id !== tenant.client_tenant_id,
  );
  const canSubmit = Boolean(chosen) && !takenByOther && selected !== currentId && !isPending;

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          Map {tenant.display_name} to a {label} {noun}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          Everything {label} holds for the {noun} you choose — devices,{' '}
          {name === 'ninjaone' ? 'tickets' : 'vulnerabilities'} — becomes this Client's. Check the
          name: a {noun} can belong to one Client only.
        </p>
      </div>
      <div className="space-y-4 px-6 py-5">
        {accounts.isPending ? (
          <Loading height="h-10" />
        ) : accounts.isError ? (
          <ErrorNote error={accounts.error} />
        ) : accounts.data.items.length === 0 ? (
          <p className="text-sm text-bluesphere-muted">
            {label} lists no {noun}s for this credential.
          </p>
        ) : (
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
              {label} {noun}
            </span>
            <select value={selected} onChange={(e) => setSelected(e.target.value)} className={INPUT}>
              <option value="">Choose…</option>
              {accounts.data.items.map((a) => {
                const other = a.mapped_to && a.mapped_to.tenant_id !== tenant.client_tenant_id;
                return (
                  <option key={a.id} value={a.id} disabled={Boolean(other)}>
                    {a.name || a.id}
                    {other ? ` — mapped to ${a.mapped_to?.name || 'another Client'}` : ''}
                  </option>
                );
              })}
            </select>
          </label>
        )}
        {chosen && !takenByOther && (
          <p className="rounded-md bg-bluesphere-surfaceMuted px-3 py-2 text-sm text-bluesphere-textOnLight">
            <strong>{tenant.display_name}</strong> ↔ <strong>{chosen.name || chosen.id}</strong>
            <span className="block text-xs text-bluesphere-muted">
              {label} {noun} id {chosen.id}
            </span>
          </p>
        )}
        <ConnectErrorNote
          error={error}
          canAddService={canAddService}
          onAddService={onAddService}
          isPending={isPending}
        />
        <div className="flex items-center justify-end gap-2 pt-2">
          <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </PillButton>
          <PillButton size="sm" disabled={!canSubmit} onClick={() => chosen && onSubmit(chosen.id)}>
            {isPending ? 'Mapping…' : 'Map'}
          </PillButton>
        </div>
      </div>
    </ModalShell>
  );
}
